/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import React, { type JSX } from 'react';
import { observable, action, when, type IReactionDisposer, makeObservable } from 'mobx';
import { observer } from 'mobx-react';

type ContextMenuProps = {
  container: React.RefObject<HTMLElement | null>;
  showOnClick?: boolean;
  onAppear?: (popupElement: HTMLElement) => boolean;
  onDisappear?: () => void;
  children(popupElement: HTMLElement): JSX.Element | null;
};

@observer
export default class ContextMenu extends React.Component<ContextMenuProps> {
  private _contextMenu: React.RefObject<HTMLDivElement | null> = React.createRef();
  private _popupMouseEvent: MouseEvent | null = null;
  private _contextMenuWhenDispower: IReactionDisposer | null = null;
  @observable private _isVisible = false;
  @observable.ref private _popupElement: HTMLElement | null = null;
  @observable private _container: React.RefObject<HTMLElement | null> | null = null;

  constructor(props: ContextMenuProps){
    super(props);
    makeObservable(this);
    this._container = props.container;
    this._contextMenuWhenDispower = when(() => !!(this._container && this._container.current), () => {
      const { container } = this.props;
      if (container.current) container.current.addEventListener('contextmenu', this._onTargetContextMenu);
      if (container.current) container.current.addEventListener('click', this._onTargetClick);
      if (container.current) container.current.addEventListener('scroll', this._onDocumentScroll);
    });
  }

  componentDidMount() {
    document.addEventListener('click', this._onDocumentClick);
    document.addEventListener('scroll', this._onDocumentScroll);
    document.addEventListener('contextmenu', this._onDocumentContextMenu, { capture: true });
    document.addEventListener('keydown', this._onKeydownPress);
    window.addEventListener('resize', this._onDocumentResize);
  }

  componentWillUnmount() {
    const { container } = this.props;
    if (container.current) container.current.removeEventListener('contextmenu', this._onTargetContextMenu);
    if (container.current) container.current.removeEventListener('click', this._onTargetClick);
    if (container.current) container.current.removeEventListener('scroll', this._onDocumentScroll);
    document.removeEventListener('click', this._onDocumentClick);
    document.removeEventListener('scroll', this._onDocumentScroll);
    document.removeEventListener('contextmenu', this._onDocumentContextMenu, { capture: true });
    document.removeEventListener('keydown', this._onKeydownPress);
    window.removeEventListener('resize', this._onDocumentResize);
    if (this._contextMenuWhenDispower){
      this._contextMenuWhenDispower();
      this._contextMenuWhenDispower = null;
    }
  }

  @action.bound
  private _onDocumentResize() {
    this._hide();
  }

  @action.bound
  private _onDocumentContextMenu(event: MouseEvent) {
    const prevElement = this._popupElement;
    this._popupElement = event.target as HTMLElement;

    if(prevElement !== this._popupElement){
      this._onDocumentScroll();
    }
  }

  componentDidUpdate(){
    this._container = this.props.container;
    this._updateMenuPosition();
  }

  render(){
    if (!this._isVisible) return null;

    return (
      <div ref={this._contextMenu} className="contextMenu" onContextMenu={(e) => this._onTargetContextMenu(e as unknown as MouseEvent)}>
        {this._popupElement && this.props.children(this._popupElement)}
      </div>
    );
  }

  @action.bound
  private _onKeydownPress(event: KeyboardEvent){
    if (event.code === 'Escape') {
      this._isVisible = false;
    }
  }

  @action.bound
  private _onTargetClick(event: MouseEvent){
    const menuTarget = (event.target as HTMLElement).closest('.context-menu-target');
    if (menuTarget && event.button === 0 /* Left */ && !this.props.showOnClick) {
      event.preventDefault();
      event.stopPropagation();
      this._onTargetContextMenu(event);
    }

    if (this.props.showOnClick){
      this._show(event);
    }
  }
  
  @action.bound
  private _onTargetContextMenu(event: MouseEvent) {
    if (this.props.showOnClick) {
      return;
    }

    event.preventDefault();
    this._show(event);
  }

  @action.bound
  private _show(event: MouseEvent){
    this._popupElement = event.currentTarget as HTMLElement;

    if (this.props.onAppear) {
      if (!this.props.onAppear(this._popupElement)){
        return;
      }
    }

    this._isVisible = true;
    this._popupMouseEvent = event;

    setTimeout(this._updateMenuPosition, 0);
  }

  @action.bound
  private _onDocumentClick(event: MouseEvent) {
    if (this.props.showOnClick) {
      //check if clicked on container
      const container = this._container?.current;
      if (container && container.contains(event.target as Node)) {
        return;
      }
    }


    const target = event.target as HTMLDivElement;
    if (!target || target.classList.contains('menu-action') || target.classList.contains('with-children')) return;
    
    if (this._isVisible || target !== this._contextMenu.current){
      this._hide();
    }
  }

  @action.bound
  private _hide(){
    this.props.onDisappear?.();
    this._isVisible = false;
  }

  @action.bound
  private _onDocumentScroll() {
    if (this._isVisible) this._hide();
  }

  @action.bound
  private _updateMenuPosition() {
    if (!this._popupMouseEvent) return;

    const screenW = window.innerWidth;
    const screenH = window.innerHeight;
    const contextMenu = this._contextMenu.current;
    if (!contextMenu) return;

    const rootW = contextMenu.offsetWidth;
    const rootH = contextMenu.offsetHeight;

    if (this.props.showOnClick) {
      // Position centered below (or above) the container element
      const container = this._container?.current;
      if (container) {
        const rect = container.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;

        // Clamp horizontally so menu stays within viewport
        const left = Math.max(5, Math.min(centerX - rootW / 2, screenW - rootW - 5));
        contextMenu.style.left = `${left}px`;

        // Show below if there's space, otherwise above
        if (screenH - rect.bottom >= rootH + 5) {
          contextMenu.style.top = `${rect.bottom + 5}px`;
        } else {
          contextMenu.style.top = `${rect.top - rootH - 5}px`;
        }
      }
      return;
    }

    const clickX = this._popupMouseEvent.clientX;
    const clickY = this._popupMouseEvent.clientY;

    const right = (screenW - clickX) > rootW;
    const top = (screenH - clickY) > rootH;

    contextMenu.style.left = right ? `${clickX + 5}px` : `${clickX - rootW - 5}px`;
    contextMenu.style.top  = top   ? `${clickY + 5}px` : `${clickY - rootH - 5}px`;
  }
}
